import transform from '@diplodoc/transform';

import { getTrackerUrl } from '@/utils/getTrackerUrl';

import { colorMap } from '../config/colors';

/**
 * Обрабатывает HTML описание задачи:
 * - Преобразует markdown через transform
 * - Обрабатывает цвета {color}(text)
 * - Обрабатывает подчеркивание ++text++
 * - Преобразует ссылки на задачи и URL
 * - Обрабатывает изображения
 */
export function processTaskDescription(description: string): string {
  // Трансформируем markdown в HTML
  const transformedHtml = transform(description, {
    allowHTML: true,
    enableMarkdownAttrs: true,
    linkify: false,
    breaks: true,
    needToSanitizeHtml: false,
  }).result.html;

  // Упрощенная обработка отступов - только для p
  let processedHtml = transformedHtml
    .replace(/<\/p>\s*<p/gi, '</p><br><p')
    // Удаляем якоря yfm-anchor, которые дублируют текст заголовков
    .replace(/<a[^>]*class="yfm-anchor"[^>]*>[\s\S]*?<\/a>/gi, '')
    // Обрабатываем заголовки - добавляем br после них (только один раз, чтобы не дублировать)
    .replace(/<\/h([1-6])>(?!<br>)/gi, '</h$1><br>');

  // Обрабатываем формат {color}(text) - цвет в фигурных скобках, текст в круглых
  // Правильно обрабатываем вложенные скобки
  processedHtml = processColorTags(processedHtml);

  // Обрабатываем ссылки на задачи и URL
  processedHtml = processLinks(processedHtml);

  // Обрабатываем пары {color}текст{/color}
  processedHtml = processedHtml.replace(/\{(\w+)\}(.*?)\{\/\1\}/gi, (_match, color, text) => {
    const cssColor = colorMap[color.toLowerCase()] || color;

    // Если это сброс цвета, просто возвращаем текст
    if (color.toLowerCase() === 'color') {
      return text;
    }

    return `<span style="color: ${cssColor}">${text}</span>`;
  });

  // Обрабатываем подчеркивание в формате ++текст++
  processedHtml = processedHtml.replace(/\+\+(.*?)\+\+/g, (_match, text) => {
    return `<span style="text-decoration: underline">${text}</span>`;
  });

  // Обрабатываем изображения: заменяем img тег на ссылку из src
  return processImages(processedHtml);
}

/**
 * Обрабатывает цветные теги в формате {color}(text) с учетом вложенных скобок
 * Использует один проход по строке с накоплением результата
 */
function processColorTags(html: string): string {
  const result: string[] = [];
  let i = 0;
  let currentText = '';

  while (i < html.length) {
    // Ищем начало цветного тега {color}(
    const tagMatch = html.substring(i).match(/^\{(\w+)\}\(/);

    if (tagMatch) {
      // Сохраняем накопленный текст перед тегом
      if (currentText) {
        result.push(currentText);
        currentText = '';
      }

      const color = tagMatch[1].toLowerCase();
      const cssColor = colorMap[color] || tagMatch[1];

      // Если это сброс цвета, пропускаем тег и скобку
      if (color === 'color') {
        i += tagMatch[0].length;
        continue;
      }

      // Пропускаем открывающий тег
      i += tagMatch[0].length;
      const textStart = i;

      // Ищем соответствующую закрывающую скобку, учитывая вложенность
      let bracketCount = 1;

      while (i < html.length && bracketCount > 0) {
        if (html[i] === '(') {
          bracketCount++;
        } else if (html[i] === ')') {
          bracketCount--;

          if (bracketCount === 0) {
            const text = html.substring(textStart, i);

            result.push(`<span style="color: ${cssColor}">${text}</span>`);
            i++; // Пропускаем закрывающую скобку

            break;
          }
        }

        i++;
      }

      // Если не нашли закрывающую скобку, добавляем как есть
      if (bracketCount > 0) {
        result.push(html.substring(textStart - tagMatch[0].length, i));
      }
    } else {
      // Обычный символ, накапливаем в буфере
      currentText += html[i];
      i++;
    }
  }

  // Добавляем оставшийся текст
  if (currentText) {
    result.push(currentText);
  }

  return result.join('');
}

/**
 * Создает HTML ссылку с безопасными атрибутами
 */
function createLink(url: string, text: string): string {
  return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
}

/**
 * Проверяет, находится ли позиция внутри HTML тега
 */
function isInsideHtmlTag(html: string, position: number): boolean {
  const before = html.substring(0, position);
  const lastOpenTag = before.lastIndexOf('<');
  const lastCloseTag = before.lastIndexOf('>');

  return lastOpenTag > lastCloseTag;
}

/**
 * Проверяет, находится ли позиция внутри ссылки <a>
 */
function isInsideLink(html: string, position: number): boolean {
  const before = html.substring(0, position);
  const lastOpenA = before.lastIndexOf('<a');
  const lastCloseA = before.lastIndexOf('</a>');

  return lastOpenA > lastCloseA;
}

/**
 * Проверяет, можно ли обрабатывать текст на данной позиции
 * (не внутри HTML тега и не внутри ссылки)
 */
function canProcessAtPosition(html: string, position: number): boolean {
  return !isInsideHtmlTag(html, position) && !isInsideLink(html, position);
}

/**
 * Обрабатывает ссылки на задачи и URL
 */
function processLinks(html: string): string {
  // Обрабатываем ссылки на задачи (формат TREK-123, JUNE-2) - только в тексте, не в тегах
  let processed = html.replace(
    /(?:^|[^>])([A-Z]+-\d+)(?![^<]*<\/a>)/g,
    (match, taskKey, offset) => {
      if (!canProcessAtPosition(html, offset)) {
        return match;
      }

      const url = getTrackerUrl(taskKey);
      const prefix = match.substring(0, match.indexOf(taskKey));

      return `${prefix}${createLink(url, taskKey)}`;
    },
  );

  // Обрабатываем обычные URL (только если они не в тегах)
  // Паттерн: http:// или https://, затем любые символы до пробела, конца строки или HTML символов
  processed = processed.replace(/(https?:\/\/[^\s<>"']+)/g, (match, url, offset, fullString) => {
    if (!canProcessAtPosition(fullString, offset)) {
      return match;
    }

    // Убираем возможные завершающие символы из URL (точка, запятая, скобки и т.д.)
    // но только если они не являются частью URL (например, точка в домене)
    const cleanUrl = url.replace(/[.,;:!?)\]}>]+$/, '');

    return createLink(cleanUrl, cleanUrl);
  });

  return processed;
}

/**
 * Обрабатывает изображения: заменяет img тег на ссылку из src
 */
function processImages(html: string): string {
  return html.replace(/<img([^>]*?)src=["']([^"']+)["']([^>]*?)>/gi, (_match, _beforeSrc, src) => {
    // Извлекаем alt текст, если есть
    const altMatch = _match.match(/alt=["']([^"']*)["']/i);
    const altText = altMatch ? altMatch[1] : 'Изображение';

    // Если путь относительный (начинается с / или ajax/), преобразуем в полный URL
    let fullUrl = src;

    if (src.startsWith('/') || src.startsWith('ajax/')) {
      fullUrl = src.startsWith('/')
        ? `https://tracker.yandex.ru${src}`
        : `https://tracker.yandex.ru/${src}`;
    }

    // Заменяем img тег на ссылку
    return createLink(fullUrl, altText);
  });
}
